import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Stack,
} from "@mui/material";
import { useWeddingWizard } from "../../../../context/WeddingWizardContext";
import api from "../../../../services/api";
import { money } from "../../../../utils/money";

// tiny utils
const fmtMoney = (n) =>
  Number.isFinite(n) ? `$${Number(n).toFixed(2)}` : null;
const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};
const fmtRange = (start, end) => {
  const a = fmtDate(start);
  const b = fmtDate(end);
  if (a && b) return `${a} → ${b}`;
  if (a) return a;
  if (b) return b;
  return null;
};
const plural = (n, s, p) => `${n} ${n === 1 ? s : p || `${s}s`}`;

// normalize "product:variant" composite ids
const splitRentalKey = (raw) => {
  const s = String(raw || "");
  const i = s.lastIndexOf(":");
  if (i === -1) return { productId: s, variantId: "" };
  return { productId: s.slice(0, i), variantId: s.slice(i + 1) };
};

export default function ReviewRentalsSummary({
  rentalLines = [],
  depositLines = [],
  hasRentals, // legacy flag; we’ll also infer from data
  total,
  depositsTotal,
}) {
  const { state } = useWeddingWizard();

  // 1) Build preliminary rows (prefer server lines; else fallback to state)
  const usingServer = Array.isArray(rentalLines) && rentalLines.length > 0;

  const prelimRows = useMemo(() => {
    if (usingServer) {
      return rentalLines
        .filter((l) => (Number(l.quantity) || 0) > 0)
        .map((l, idx) => {
          const baseId = String(l.productId || l.id || "");
          const { productId, variantId } =
            l.productId && l.variantId
              ? {
                  productId: String(l.productId),
                  variantId: String(l.variantId),
                }
              : splitRentalKey(baseId);

          return {
            key: `${productId || "rental"}-${idx}`,
            productId,
            variantId,
            displayName: l.displayName || l.name || l.title || "",
            variantName: l.variantName || "",
            quantity: Number(l.quantity) || 0,
            startDate: l.startDate || null,
            endDate: l.endDate || null,
            days: Number(l.days) || null,
            unitPrice: Number(l.unitPrice),
            lineTotal: Number(l.lineTotal ?? l.subtotal ?? l.total),
            depositAmount: Number(l.depositAmount ?? l.refundableDeposit),
            customizations: Array.isArray(l.customizations)
              ? l.customizations
              : [],
          };
        });
    }

    const items = Array.isArray(state?.rentals?.items)
      ? state.rentals.items
      : [];
    return items
      .filter((it) => (Number(it.quantity) || 0) > 0 && (it.productId || it.id))
      .map((it, idx) => {
        const { productId, variantId } =
          it.productId && it.variantId
            ? {
                productId: String(it.productId),
                variantId: String(it.variantId),
              }
            : splitRentalKey(String(it.id || ""));
        return {
          key: `${productId}:${variantId}-${idx}`,
          productId,
          variantId,
          // prefer snapshots saved at selection time
          displayName:
            it.productNameSnapshot ||
            it.displayNameSnapshot ||
            it.displayName ||
            "",
          variantName:
            it.variantNameSnapshot ||
            it.variantDisplayName ||
            it.variantName ||
            "",
          quantity: Number(it.quantity) || 0,
          startDate: it.startDate || null,
          endDate: it.endDate || null,
          days: Number(it.days) || null,
          unitPrice: Number(it.unitPrice), // may be undefined; fine
          lineTotal: Number(it.lineTotal ?? it.subtotal ?? it.total),
          depositAmount: Number(it.depositAmount ?? it.refundableDeposit),
          customizations: Array.isArray(it.customizations)
            ? it.customizations
            : [],
        };
      });
  }, [usingServer, rentalLines, state?.rentals?.items]);

  // 2) Lookup is ONLY for client-fallback rows (never when server lines are present)
  const needsLookup = useMemo(
    () =>
      !usingServer && prelimRows.some((r) => !r.displayName || !r.variantName),
    [usingServer, prelimRows]
  );

  const [catalog, setCatalog] = useState({ byProduct: {}, byVariant: {} });

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!needsLookup) return;

      // gather only the productIds we’re missing names for
      const missingProductIds = Array.from(
        new Set(
          prelimRows
            .filter((r) => (!r.displayName || !r.variantName) && r.productId)
            .map((r) => r.productId)
        )
      );
      if (missingProductIds.length === 0) return;

      try {
        // fetch each product by business id
        const results = await Promise.allSettled(
          missingProductIds.map((pid) =>
            api.get(`/rentals/products/${encodeURIComponent(pid)}`)
          )
        );

        const byProduct = {};
        const byVariant = {};

        for (const res of results) {
          if (res.status !== "fulfilled") continue;
          const p = res.value?.data;
          const pid = String(p?.id || p?._id || "");
          if (!pid) continue;

          byProduct[pid] = p?.displayName || p?.title || p?.name || pid;

          const variants = Array.isArray(p?.variants) ? p.variants : [];
          for (const v of variants) {
            const vid = String(v?.id || v?._id || "");
            if (!vid) continue;
            byVariant[`${pid}:${vid}`] =
              v?.displayName || v?.title || v?.name || vid;
          }
        }

        if (alive) setCatalog({ byProduct, byVariant });
      } catch (e) {
        console.warn("Rentals catalog lookup failed", e);
        if (alive) setCatalog({ byProduct: {}, byVariant: {} });
      }
    })();
    return () => {
      alive = false;
    };
  }, [needsLookup, prelimRows]);

  // 3) Final rows with best-available names and formatted fields
  const rows = useMemo(() => {
    return prelimRows.map((r) => {
      const name =
        r.displayName ||
        catalog.byProduct[r.productId] ||
        r.productId ||
        "Rental";
      const varName =
        r.variantName ||
        catalog.byVariant[`${r.productId}:${r.variantId}`] ||
        "";

      const variant =
        varName ||
        (r.customizations.length
          ? r.customizations
              .map((c) => [c?.label, c?.value].filter(Boolean).join(": "))
              .filter(Boolean)
              .join(" • ")
          : "");

      const range = fmtRange(r.startDate, r.endDate);
      const days =
        Number.isFinite(r.days) && r.days > 0
          ? r.days
          : r.startDate && r.endDate
          ? Math.max(
              1,
              Math.ceil(
                (new Date(r.endDate) - new Date(r.startDate)) /
                  (1000 * 60 * 60 * 24)
              )
            )
          : null;

      return {
        key: r.key,
        name,
        variant,
        qty: r.quantity > 0 ? r.quantity : null,
        range,
        days,
        unitMoney: fmtMoney(r.unitPrice),
        totalMoney: fmtMoney(r.lineTotal),
        depositMoney: fmtMoney(r.depositAmount),
      };
    });
  }, [prelimRows, catalog]);

  // 4) Deposits list (aggregate section); if depositLines empty, we skip it
  const deposits = useMemo(() => {
    return (depositLines || []).map((line, idx) => ({
      key: `${line.productId || line.id || "dep"}-${idx}`,
      name:
        line.displayName ||
        line.name ||
        line.title ||
        line.productId ||
        "Deposit",
      money: fmtMoney(Number(line.amount ?? line.depositAmount ?? line.total)),
    }));
  }, [depositLines]);

  // 5) Only show “No rentals added” when we truly have nothing to show
  const showEmpty = rows.length === 0 && (!hasRentals || !prelimRows.length);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
      <CardContent sx={{ p: 2.25 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Rentals
          </Typography>

          <Stack spacing={0} alignItems="flex-end">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {money(total)}
            </Typography>
            {Number(depositsTotal) > 0 && (
              <Typography variant="caption" color="text.secondary">
                Deposit: {money(depositsTotal)}
              </Typography>
            )}
          </Stack>
        </Stack>

        {!showEmpty ? (
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            {rows.map((r) => (
              <Grid item xs={12} sm={6} key={r.key}>
                {/* Title + qty + total */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {r.name}
                    {r.qty ? ` × ${r.qty}` : ""}
                  </Typography>
                  {r.totalMoney ? (
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {r.totalMoney}
                    </Typography>
                  ) : null}
                </Stack>

                {/* Variant/customization */}
                {r.variant ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}>
                    {r.variant}
                  </Typography>
                ) : null}

                {/* Dates + days */}
                {(r.range || r.days) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}>
                    {r.range ? `${r.range}` : ""}
                    {r.range && r.days ? " • " : ""}
                    {r.days ? plural(r.days, "day") : ""}
                  </Typography>
                )}

                {/* Unit price (optional) */}
                {r.unitMoney ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}>
                    {`Rate: ${r.unitMoney}${r.days ? " / day" : ""}`}
                  </Typography>
                ) : null}

                {/* Per-line refundable deposit (optional) */}
                {r.depositMoney ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}>
                    {`Refundable deposit: ${r.depositMoney}`}
                  </Typography>
                ) : null}
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            No rentals added.
          </Typography>
        )}

        {/* Aggregate deposits section */}
        {deposits.length > 0 && (
          <>
            <Divider sx={{ my: 1.25 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Refundable deposits
            </Typography>
            <Grid container spacing={1} sx={{ mt: 0.5 }}>
              {deposits.map((d) => (
                <Grid item xs={12} sm={6} key={d.key}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}>
                    <Typography variant="body2">{d.name}</Typography>
                    {d.money ? (
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {d.money}
                      </Typography>
                    ) : null}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </CardContent>
    </Card>
  );
}
